/**
 * Toast primitives — Radix's `react-toast` model with a paired
 * `useToaster` hook that owns the queue (mirrors the shadcn pattern).
 *
 * Usage:
 *   const { toast } = useToaster();
 *   toast({ title: 'Saved', description: 'Your changes are live.' });
 *
 * <Toaster /> must be mounted exactly once (in __root.tsx).
 */
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  MUTATION_ERROR_TOAST_EVENT,
  type MutationErrorToastDetail,
} from '../../lib/api';
import { cn } from '../../lib/utils';

const toastVariants = cva(
  [
    'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all',
    'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full',
  ],
  {
    variants: {
      variant: {
        default: 'border-border bg-background text-foreground backdrop-blur-sm',
        success: 'border-success bg-background text-success-onTint backdrop-blur-sm',
        warning: 'border-warning bg-background text-warning-onTint backdrop-blur-sm',
        destructive: 'border-destructive bg-background text-destructive backdrop-blur-sm',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef<
  ElementRef<typeof ToastPrimitive.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      // Safe-area bottom padding so toasts clear the iOS home indicator; dvh so
      // the cap is correct when the mobile browser toolbar is showing.
      'fixed bottom-0 z-[100] flex max-h-dvh w-full flex-col p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:right-0 sm:flex-col sm:pb-4 md:max-w-[420px]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

export const Toast = forwardRef<
  ElementRef<typeof ToastPrimitive.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitive.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props} />
));
Toast.displayName = ToastPrimitive.Root.displayName;

export const ToastAction = forwardRef<
  ElementRef<typeof ToastPrimitive.Action>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

export const ToastClose = forwardRef<
  ElementRef<typeof ToastPrimitive.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    toast-close=""
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100',
      className,
    )}
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

export const ToastTitle = forwardRef<
  ElementRef<typeof ToastPrimitive.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

export const ToastDescription = forwardRef<
  ElementRef<typeof ToastPrimitive.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

// === Toaster + useToaster hook ============================================
export interface ToastActionConfig {
  /** Label shown inside the action button (e.g. "Undo"). */
  label: ReactNode;
  /** Invoked on click. The toast dismisses immediately when the user
   *  presses the action — we don't wait for the handler to resolve so
   *  the UI stays responsive for slow restore calls. */
  onClick: () => void | Promise<void>;
  /** Optional ARIA label override (falls back to label as string). */
  ariaLabel?: string;
}

export interface ToastItem {
  id: string;
  title?: ReactNode;
  description?: ReactNode;
  variant?: VariantProps<typeof toastVariants>['variant'];
  durationMs?: number;
  /** Optional inline action button (e.g. "Undo"). */
  action?: ToastActionConfig;
}

export interface ToasterCtx {
  toasts: ToastItem[];
  toast: (item: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
}

const ToasterContext = createContext<ToasterCtx | undefined>(undefined);

export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((item: Omit<ToastItem, 'id'>): string => {
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((current) => [...current, { id, ...item }]);
    return id;
  }, []);

  const value = useMemo<ToasterCtx>(() => ({ toasts, toast, dismiss }), [toasts, toast, dismiss]);
  return <ToasterContext.Provider value={value}>{children}</ToasterContext.Provider>;
}

export function useToaster(): ToasterCtx {
  const ctx = useContext(ToasterContext);
  if (!ctx) throw new Error('useToaster must be used inside <ToasterProvider>');
  return ctx;
}

/**
 * The toaster when a <ToasterProvider> is mounted, otherwise null.
 *
 * For leaf primitives that are legitimately rendered outside the app shell
 * (unit tests mount <CopyButton> inside a bare <details> or message row).
 * Those must degrade to "no toast", never crash the tree.
 */
export function useToasterOptional(): ToasterCtx | null {
  return useContext(ToasterContext) ?? null;
}

export function Toaster() {
  const { toasts, toast, dismiss } = useToaster();

  // Bridge for the global MutationCache onError (lib/api): it dispatches a
  // window CustomEvent because it runs outside React and can't call the
  // toaster hook directly. We turn that into the single generic
  // destructive toast for unhandled mutation failures.
  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<MutationErrorToastDetail>).detail;
      toast({
        variant: 'destructive',
        title: detail?.title ?? 'Something went wrong',
        description: detail?.description ?? 'Please try again.',
      });
    };
    window.addEventListener(MUTATION_ERROR_TOAST_EVENT, handler);
    return () => window.removeEventListener(MUTATION_ERROR_TOAST_EVENT, handler);
  }, [toast]);

  return (
    <ToastProvider swipeDirection="right">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          variant={t.variant ?? 'default'}
          duration={t.durationMs ?? 5_000}
          onOpenChange={(open) => {
            if (!open) dismiss(t.id);
          }}
        >
          <div className="grid gap-1">
            {t.title && <ToastTitle>{t.title}</ToastTitle>}
            {t.description && <ToastDescription>{t.description}</ToastDescription>}
          </div>
          {t.action && (
            <ToastAction
              altText={typeof t.action.label === 'string' ? t.action.label : 'Action'}
              aria-label={
                t.action.ariaLabel ??
                (typeof t.action.label === 'string' ? t.action.label : undefined)
              }
              onClick={() => {
                // Dismiss first so the toast doesn't sit while the
                // mutation runs; handler errors are surfaced via the
                // mutation's own onError pathway.
                dismiss(t.id);
                void t.action?.onClick();
              }}
            >
              {t.action.label}
            </ToastAction>
          )}
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
